import useAnalytics from "@hooks/useAnalytics";
import useApiFetch from "@hooks/useApiFetch";
import useClientContext, { setModal } from "@hooks/useClientContext";
import { SubmitHandler, useForm } from "react-hook-form";
import { pxToRem } from "src/styles/styleUtils";
import styled, { css } from "styled-components";
import Button from "./Button";
import { ChaseErrorArea, ChaseInput, ChaseLabel, ChaseStackedRow } from "./Form";
import Modal, {
  ChaseContentContainer,
  ChaseHeaderContent,
  ChaseModalWrapper,
  playoffsModalCSS,
} from "./Modal";

interface InviteInput {
  email: string;
  message?: string | null;
}

function InviteModal({ leagueId }) {
  const [{ openModal }, dispatch] = useClientContext();
  const shouldRenderModal = openModal.name === "INVITE_MODAL";
  const { fetchApiEndpoint } = useApiFetch();
  const { trackEvent } = useAnalytics();
  const defaultError = "Invalid input, please try again.";

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset: resetForm,
  } = useForm<InviteInput>();

  function sendEmailInvite(data: InviteInput) {
    const body = { ...data };
    return fetchApiEndpoint(`/api/v1/chase/leagues/${leagueId}/invite.json`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  const onSubmit: SubmitHandler<InviteInput> = (data) => {
    sendEmailInvite(data).then(
      () => {
        dispatch(setModal(null));
        trackEvent("league_invite:email");
        resetForm();
      },
      (e) => {
        setError("email", {
          type: "manual",
          message: `${e}`,
        });
      },
    );
  };

  return shouldRenderModal ? (
    <Modal aria-label="Invite to League Dialog" css={modalCSS}>
      <ChaseModalWrapper>
        <ChaseContentContainer>
          <ChaseHeaderContent>
            <h2>Invite to League</h2>
            <form onSubmit={handleSubmit(onSubmit)}>
              <ChaseStackedRow>
                <ChaseLabel>Email Addresses</ChaseLabel>
                <ChaseInput
                  type="text"
                  {...register("email", {
                    required: "* Email is required",
                  })}
                  placeholder="(Comma Separated)"
                />
              </ChaseStackedRow>
              {errors.email && (
                <ChaseErrorArea>{errors.email?.message || defaultError}</ChaseErrorArea>
              )}
              <ChaseStackedRow>
                <ChaseLabel>Additional Message</ChaseLabel>
                <MessageInput {...register("message")} placeholder="(Optional)" />
              </ChaseStackedRow>
              <ChaseStackedRow>
                <div>
                  <Button type="submit">Send Invite</Button>
                </div>
              </ChaseStackedRow>
            </form>
          </ChaseHeaderContent>
        </ChaseContentContainer>
      </ChaseModalWrapper>
    </Modal>
  ) : null;
}

export default InviteModal;

const modalCSS = css`
  ${playoffsModalCSS}

  .closeIcon {
    top: ${pxToRem(3)};
    right: ${pxToRem(2)};
    svg {
      width: ${pxToRem(14)};
    }
  }
`;

const MessageInput = styled.textarea`
  min-height: 4rem;
  padding: ${pxToRem(8)};
  background: transparent;
  border: ${pxToRem(1)} solid white;
  border-radius: 0px;
  color: white;
  font-size: ${pxToRem(14)};

  &:focus-visible {
    outline: none;
  }

  &:focus {
    color: white !important;
  }
`;
